import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Eye, Loader2 } from 'lucide-react';
import { BrandMyProposal, getMyBrandProposals, withdrawBrandProposal } from '../../api/payment/brand-deal.api';
import { proposalStatusLabel } from '../../utils/brand-proposal';
import { routePaths } from '../../routes/routes-path';
import { IndividualOrdererBadge } from './orderer-badge';
import { BrandThreadToggle } from './brand-thread-panel';
import TurnPill from './turn-pill';
import { sentRequestTurn } from '../../utils/brand-turn';

/** Tab "Đề xuất của tôi" (creator, spec 2026-10-12 §6): trạng thái, "Người đặt đã xem", lý do, rút khi còn chờ, mở việc khi được chọn. */

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

const formatDate = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Khai ngoài component cha (landmine remount).
const ProposalRow: React.FC<{ p: BrandMyProposal; busy: boolean; onWithdraw: () => void }> = ({ p, busy, onWithdraw }) => (
  <div className="flex flex-col gap-2 p-4 text-[14px]">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="min-w-0">
        <div className="ui-heading font-bold">{p.campaignTitle}</div>
        <div className="ui-muted flex items-center gap-2 text-[13px]">
          {p.brandName} <IndividualOrdererBadge kind={p.ordererKind} /> · {formatDate(p.createdAt)}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {p.viewedByOrdererAt && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[12px] font-semibold text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300">
            <Eye size={12} /> Người đặt đã xem
          </span>
        )}
        <span className="flex items-center gap-2">
          <TurnPill turn={sentRequestTurn(p.status)} />
          <span className="ui-divider rounded-full border px-2.5 py-1 text-[12px] font-semibold ui-text">{proposalStatusLabel(p.status)}</span>
        </span>
      </div>
    </div>
    {p.declineReason && <p className="text-[13px] text-amber-600 dark:text-amber-400">Lý do: {p.declineReason}</p>}
    {p.expireReason && <p className="text-[13px] text-amber-600 dark:text-amber-400">{p.expireReason}</p>}
    <div className="flex flex-wrap items-center gap-3">
      {p.status === 'Pending' && (
        <button type="button" disabled={busy} onClick={onWithdraw} className="rounded-md border border-rose-200 px-3 py-1.5 text-[13px] font-semibold text-rose-600 disabled:opacity-60 dark:border-rose-900/40">
          Rút đề xuất
        </button>
      )}
      {p.status === 'Accepted' && p.dealId && (
        <Link to={`${routePaths.brandDeals}/task/${p.dealId}`} className="font-semibold text-[#0057FF]">
          Mở việc
        </Link>
      )}
      <BrandThreadToggle threadId={p.threadId} />
    </div>
  </div>
);

const BrandMyProposals: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<BrandMyProposal[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    getMyBrandProposals()
      .then(setItems)
      .catch(() => toast.error('Không tải được đề xuất của bạn.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const withdraw = async (p: BrandMyProposal) => {
    if (!window.confirm('Rút đề xuất này? Bạn có thể gửi đề xuất khác khi chiến dịch còn nhận.')) return;
    setBusyId(p.id);
    try {
      await withdrawBrandProposal(p.id);
      toast.success('Đã rút đề xuất.');
      load();
    } catch (err) {
      toast.error(errorMessage(err, 'Không rút được đề xuất.'));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="animate-spin ui-muted" />
      </div>
    );
  }
  if (items.length === 0) return <p className="ui-muted p-4 text-center text-[14px]">Bạn chưa gửi đề xuất nào.</p>;

  return (
    <div className="ui-card divide-y ui-divider">
      {items.map((p) => (
        <ProposalRow key={p.id} p={p} busy={busyId === p.id} onWithdraw={() => withdraw(p)} />
      ))}
    </div>
  );
};

export default BrandMyProposals;
